import { useEffect, useState } from 'react';
import {
  appendFollowUp,
  openFollowUp,
  type FollowUpMessage,
  type OpenFollowUpResult,
} from '@/lib/firebase';

/**
 * 「이어서 문의하기」 화면 (②번 · 2026-09-09).
 *
 * 답변 알림(알림톡·이메일)의 버튼으로 들어온 고객이 **지난 답변을 다시 보고 같은 티켓에 이어 쓴다.**
 *   - 이름·연락처를 **다시 묻지 않는다** — 이미 아는 값이다. 여기서 또 물으면 그냥 새로 접수하는
 *     것과 다를 게 없고, 고객은 그 편을 택한다(그러면 티켓이 또 갈라진다).
 *   - 실패 사유를 **구분해서** 보여 준다. "안 열립니다" 하나로 뭉치면 고객은 무엇을 해야 할지 모른다.
 *   - 기간이 지난 링크는 **오류가 아니라 안내**다 — 「새로 문의하기」로 이어 준다.
 */
export function FollowUpPanel({ fkey, onNewInquiry }: { fkey: string; onNewInquiry: () => void }) {
  const [state, setState] = useState<{ loading: boolean; data?: OpenFollowUpResult; error?: string }>(
    { loading: true },
  );
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');
  // 보낸 뒤 화면에 바로 얹는 낙관적 사본 — 다시 열어 받아 오지 않아도 «갔다»가 보인다.
  const [appended, setAppended] = useState<FollowUpMessage[]>([]);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    void openFollowUp(fkey)
      .then((data) => {
        if (!cancelled) setState({ loading: false, data });
      })
      .catch((err) => {
        console.error('[widget] 이어쓰기 조회 실패', err);
        if (!cancelled) setState({ loading: false, error: '문의를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.' });
      });
    return () => {
      cancelled = true;
    };
  }, [fkey]);

  async function submit() {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError('');
    try {
      await appendFollowUp(fkey, body);
      setAppended((prev) => [...prev, { role: 'customer', text: body, atMs: Date.now() }]);
      setText('');
      setSent(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setSendError(msg || '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSending(false);
    }
  }

  if (state.loading) {
    return <p className="p-6 text-center text-sm text-text-tertiary">불러오는 중…</p>;
  }
  if (state.error) {
    return <Notice title="일시적인 오류" body={state.error} onNewInquiry={onNewInquiry} />;
  }
  const d = state.data;
  if (!d || !d.ok) {
    // 🔴 만료와 «잘못된 링크»를 갈라 말한다 — 할 수 있는 일이 서로 다르다.
    return d?.reason === 'expired' ? (
      <Notice
        title="이어서 문의할 수 있는 기간이 지났습니다"
        body="접수일로부터 30일이 지난 문의입니다. 아래에서 새로 문의해 주세요."
        onNewInquiry={onNewInquiry}
      />
    ) : (
      <Notice
        title="문의를 찾을 수 없습니다"
        body="링크가 잘못되었거나 만료되었습니다. 아래에서 새로 문의해 주세요."
        onNewInquiry={onNewInquiry}
      />
    );
  }

  const messages = [...d.messages, ...appended];
  const full = d.appendCount + appended.length >= d.maxAppends;

  return (
    <div className="flex flex-col gap-3 p-4">
      <div>
        <h2 className="text-base font-semibold text-text-primary">
          {d.type === 'lost_item' && d.itemName ? `[${d.itemName}] ` : ''}
          이어서 문의하기
        </h2>
        <p className="mt-0.5 text-xs text-text-tertiary">
          {d.tenantName ? `${d.tenantName} · ` : ''}접수번호 <span className="font-mono">{d.receiptNo}</span>
          {d.customerName ? ` · ${d.customerName}님` : ''}
        </p>
      </div>

      {/* 지난 대화 — 고객이 «무엇을 물었고 무엇을 답받았는지» 를 다시 읽고 이어 쓸 수 있게 */}
      <div className="flex flex-col gap-2 max-h-[45vh] overflow-y-auto rounded-lg bg-muted p-3">
        {messages.length === 0 ? (
          <p className="text-xs text-text-tertiary">표시할 내용이 없습니다.</p>
        ) : (
          messages.map((m, i) => (
            <div
              key={`${m.atMs}-${i}`}
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                m.role === 'agent'
                  ? 'self-start bg-surface border border-border-subtle text-text-primary'
                  : m.role === 'bot'
                    ? 'self-start bg-surface border border-border-subtle text-text-secondary'
                    : 'self-end bg-accent text-white'
              }`}
            >
              {m.text}
            </div>
          ))
        )}
      </div>

      {sent && (
        <p className="rounded-md bg-success-soft px-3 py-2 text-sm text-success">
          추가 문의가 접수되었습니다. 답변이 등록되면 다시 알려 드리겠습니다.
        </p>
      )}

      {full ? (
        <Notice
          title="이 문의에는 더 이상 추가할 수 없습니다"
          body="내용이 많이 쌓였습니다. 새로 문의해 주시면 더 빠르게 도와드릴 수 있습니다."
          onNewInquiry={onNewInquiry}
        />
      ) : (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            maxLength={3000}
            placeholder="답변에 대해 더 궁금한 점을 적어 주세요."
            className="w-full rounded-md border border-border-default bg-surface px-3 py-2 text-sm focus:border-accent focus:outline-none"
          />
          {sendError && <p className="text-xs text-danger">{sendError}</p>}
          <button
            type="button"
            disabled={!text.trim() || sending}
            onClick={() => void submit()}
            className="w-full rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {sending ? '전송 중…' : '이어서 문의 보내기'}
          </button>
          <button
            type="button"
            onClick={onNewInquiry}
            className="text-xs text-text-tertiary underline underline-offset-2"
          >
            다른 내용으로 새로 문의하기
          </button>
        </>
      )}
    </div>
  );
}

/** 안내 화면 — 막다른 길로 두지 않고 항상 「새로 문의하기」를 함께 준다. */
function Notice({ title, body, onNewInquiry }: { title: string; body: string; onNewInquiry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 p-8 text-center">
      <p className="text-sm font-semibold text-text-primary">{title}</p>
      <p className="text-xs text-text-secondary leading-relaxed">{body}</p>
      <button
        type="button"
        onClick={onNewInquiry}
        className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white"
      >
        새로 문의하기
      </button>
    </div>
  );
}
